import { z } from 'zod'
import { productValidationMessages } from '../../../messages'

export const getHsLocalTariffValidationSchema = (formatMessage: any) => {
  return z.object({
    id: z.number().optional(),
    description: z.string().min(1, {
      message: formatMessage({
        id: 'validation.required',
        defaultMessage: 'Description is required',
      }),
    }),
    productCategoryId: z.number().optional(),
    countryId: z.number({
      required_error: formatMessage({
        id: 'validation.required',
        defaultMessage: 'Import Country is required',
      }),
    }),
    tariff: z
      .number({
        required_error: formatMessage({
          id: 'validation.required',
          defaultMessage: 'Tariff is required',
        }),
      })
      .max(99999.9999, {
        message: formatMessage({
          id: 'validation.maxValue',
          defaultMessage: 'Max value 99,999.9999',
        }),
      })
      .positive({
        message: formatMessage({
          id: 'validation.positive',
          defaultMessage: 'Value must be positive',
        }),
      }),
    classificationCode: z.string().optional(),
    hsSubheadingId: z.number().optional(),
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    updatedByUserId: z.number().optional(),
  })
}

export const getProductCategoryValidationSchema = (formatMessage: any) => {
  const hsLocalTariffSchema = z.object({
    id: z.number().optional(),
    description: z.string().min(1, { message: 'Description is required' }),
    productCategoryId: z.number().optional(),
    countryId: z.number({ required_error: 'Import Country is required' }),
    tariff: z
      .number({ required_error: 'Tariff is required' })
      .max(99999.9999, { message: 'Max value 99,999.9999' })
      .optional(),
    classificationCode: z.string().optional(),
    hsSubheadingId: z.number().optional(),
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    updatedByUserId: z.number().optional(),
  })

  const temperatureSettingSchema = z
    .object({
      temperatureRangeId: z.number().nullable().optional(),
      setPoint: z.number().nullable().optional(),
      temperatureUnitId: z.number().nullable().optional(),
      lowerThresholdWarning: z.number().nullable().optional(),
      upperThresholdWarning: z.number().nullable().optional(),
      lowerThresholdCritical: z.number().nullable().optional(),
      upperThresholdCritical: z.number().nullable().optional(),
    })
    .nullable()
    .optional()
    .superRefine((data, ctx) => {
      if (!data) return

      if (
        data.setPoint !== undefined ||
        data.upperThresholdWarning !== undefined ||
        data.upperThresholdCritical !== undefined ||
        data.lowerThresholdWarning !== undefined ||
        data.lowerThresholdCritical !== undefined
      ) {
        if (!data.temperatureUnitId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.temperatureUnitRequired),
            path: ['setPoint'],
          })
        }
      }

      if (data.temperatureRangeId !== undefined && data.temperatureRangeId !== null) {
        if (data.setPoint === undefined || data.setPoint === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.setPointRequired),
            path: ['setPoint'],
          })
        }
        if (data.lowerThresholdCritical === undefined || data.lowerThresholdCritical === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.criticalThresholdsRequired),
            path: ['lowerThresholdCritical'],
          })
        }
        if (data.upperThresholdCritical === undefined || data.upperThresholdCritical === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(productValidationMessages.criticalThresholdsRequired),
            path: ['upperThresholdCritical'],
          })
        }
      }

      if (
        data.setPoint !== undefined &&
        data.setPoint !== null &&
        data.upperThresholdCritical !== undefined &&
        data.upperThresholdCritical !== null &&
        data.setPoint > data.upperThresholdCritical
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.setPointUpperCriticalComparison),
          path: ['setPoint'],
        })
      }

      if (
        data.upperThresholdCritical !== undefined &&
        data.upperThresholdCritical !== null &&
        data.setPoint !== undefined &&
        data.setPoint !== null &&
        data.upperThresholdCritical < data.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.upperCriticalSetPointComparison),
          path: ['upperThresholdCritical'],
        })
      }

      if (
        data.lowerThresholdCritical !== undefined &&
        data.lowerThresholdCritical !== null &&
        data.setPoint !== undefined &&
        data.setPoint !== null &&
        data.lowerThresholdCritical > data.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.lowerCriticalSetPointComparison),
          path: ['lowerThresholdCritical'],
        })
      }

      if (
        data.lowerThresholdWarning !== undefined &&
        data.lowerThresholdWarning !== null &&
        data.lowerThresholdCritical !== undefined &&
        data.lowerThresholdCritical !== null &&
        data.lowerThresholdWarning < data.lowerThresholdCritical
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.lowerWarningLowerCriticalComparison),
          path: ['lowerThresholdWarning'],
        })
      }

      if (
        data.upperThresholdWarning !== undefined &&
        data.upperThresholdWarning !== null &&
        data.setPoint !== undefined &&
        data.setPoint !== null &&
        data.upperThresholdWarning < data.setPoint
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.upperWarningSetPointComparison),
          path: ['upperThresholdWarning'],
        })
      }

      if (
        data.lowerThresholdWarning !== undefined &&
        data.lowerThresholdWarning !== null &&
        data.upperThresholdWarning !== undefined &&
        data.upperThresholdWarning !== null &&
        data.lowerThresholdWarning > data.upperThresholdWarning
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.thresholdWarningComparison),
          path: ['lowerThresholdWarning'],
        })
      }

      if (
        data.lowerThresholdCritical !== undefined &&
        data.lowerThresholdCritical !== null &&
        data.upperThresholdCritical !== undefined &&
        data.upperThresholdCritical !== null &&
        data.lowerThresholdCritical > data.upperThresholdCritical
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(productValidationMessages.thresholdCriticalComparison),
          path: ['lowerThresholdCritical'],
        })
      }
    })

  return z.object({
    id: z.number().optional(),
    customerId: z.number().optional(),
    name: z
      .string()
      .min(1, { message: 'Product Category Name is required' })
      .max(250, { message: 'Product Category Name must not exceed 250 characters' }),
    hsHeadingId: z.number().nullable(),
    hsSubheadingId: z.number().nullable(),
    hsLocalTariffs: z.array(hsLocalTariffSchema).optional(),
    temperatureSetting: temperatureSettingSchema,
    validFrom: z.string().optional(),
    validTo: z.string().optional(),
    updatedByUserId: z.number().optional(),
  })
}
